import { Injectable, OnInit } from "@angular/core";
import { forkJoin, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { CONSTANT } from "src/app/constant";
import { MasterData } from "src/app/models/models";
import {
	enrichMasterDataWithAocCatalog,
	masterDataNeedsAocCatalogRefresh,
} from "src/app/shared/utils/master-catalog.utils";
import { SESSION } from "src/session.config";
import { HttpService } from "./http.service";
import { LoaderService } from "./loader.service";

@Injectable({
	providedIn: "root",
})
export class MasterDataService implements OnInit {
	private ensureReadyPromise?: Promise<boolean>;
	private masterInitPromise?: Promise<void>;
	private formMasterInitPromise?: Promise<void>;

	constructor(
		private loaderService: LoaderService,
		private httpServices: HttpService,
	) {}

	/** True when both master-data caches are present and parseable in localStorage. */
	hasCachedMasterData(): boolean {
		return (
			this.hasUsableCache(SESSION.SESSION_KEY_MASTER_DATA) &&
			this.hasUsableCache(SESSION.SESSION_KEY_FORM_MASTER_DATA) &&
			!this.masterCacheNeedsCatalogRefresh()
		);
	}

	/**
	 * Ensures master + form-master caches are available for rating forms.
	 * Uses existing localStorage when present; fetches when online and missing;
	 * returns false offline (or on failed fetch) when caches are absent.
	 * Concurrent callers share one in-flight hydrate (including configure()'s inits).
	 */
	async ensureReady(): Promise<boolean> {
		if (this.hasCachedMasterData()) {
			return true;
		}
		if (this.ensureReadyPromise) {
			return this.ensureReadyPromise;
		}

		this.ensureReadyPromise = this.hydrateMissingCaches().finally(() => {
			this.ensureReadyPromise = undefined;
		});
		return this.ensureReadyPromise;
	}

	/** Atomic: never clear before a successful write. Shares in-flight work with ensureReady/configure. */
	async initMasterData(): Promise<void> {
		if (this.masterInitPromise) {
			return this.masterInitPromise;
		}
		this.masterInitPromise = this.fetchMasterData().finally(() => {
			this.masterInitPromise = undefined;
		});
		return this.masterInitPromise;
	}

	/** Atomic: never clear before a successful write. Shares in-flight work with ensureReady/configure. */
	async initFormMasterData(): Promise<void> {
		if (this.formMasterInitPromise) {
			return this.formMasterInitPromise;
		}
		this.formMasterInitPromise = this.fetchFormMasterData().finally(() => {
			this.formMasterInitPromise = undefined;
		});
		return this.formMasterInitPromise;
	}

	ngOnInit(): void {
		void this.initMasterData();
		void this.initFormMasterData();
	}

	private async hydrateMissingCaches(): Promise<boolean> {
		if (!navigator.onLine) {
			return this.hasCachedMasterData();
		}

		const needMaster =
			!this.hasUsableCache(SESSION.SESSION_KEY_MASTER_DATA) || this.masterCacheNeedsCatalogRefresh();
		const needFormMaster = !this.hasUsableCache(SESSION.SESSION_KEY_FORM_MASTER_DATA);

		if (needMaster) {
			await this.initMasterData();
		}
		if (needFormMaster) {
			await this.initFormMasterData();
		}

		return this.hasCachedMasterData();
	}

	private fetchMasterData(): Promise<void> {
		return new Promise<void>((resolve) => {
			this.loaderService.setLoading(true);

			this.httpServices.formsMasterValues().subscribe({
				next: (resp: any) => {
					console.log("master data", resp.data);
					if (resp?.data != null) {
						const enriched = enrichMasterDataWithAocCatalog(resp.data as MasterData) ?? resp.data;
						localStorage.setItem(SESSION.SESSION_KEY_MASTER_DATA, JSON.stringify(enriched));
					}
					this.loaderService.setLoading(false);
					resolve();
				},
				error: () => {
					// Keep previous cache intact on failure.
					this.loaderService.setLoading(false);
					resolve();
				},
			});
		});
	}

	private fetchFormMasterData(): Promise<void> {
		return new Promise<void>((resolve) => {
			const formTypes = [
				{ code: CONSTANT.FORM_TYPE.ts_1 },
				{ code: CONSTANT.FORM_TYPE.ts_3 },
				{ code: CONSTANT.FORM_TYPE.ts_5 },
				{ code: CONSTANT.FORM_TYPE.ts_6 },
				{ code: CONSTANT.FORM_TYPE.cabin_crew },
				{ code: CONSTANT.FORM_TYPE.cabin_crew_instructor },
			];

			this.loaderService.setLoading(true);

			const requests = formTypes.map((ft) =>
				this.httpServices.getFormConfig(ft).pipe(
					map((resp: any) => [ft.code, resp?.data ?? resp]),
					catchError(() => of<[string, any]>([ft.code, null])),
				),
			);

			forkJoin(requests).subscribe({
				next: (pairs) => {
					const cache: Record<string, any> = {};
					for (const [code, payload] of pairs) {
						if (payload) cache[code] = payload;
					}
					// Only replace cache when we got at least one form config; keep prior on total failure.
					if (Object.keys(cache).length > 0) {
						localStorage.setItem(SESSION.SESSION_KEY_FORM_MASTER_DATA, JSON.stringify(cache));
					}
					this.loaderService.setLoading(false);
					console.log("formMasterData", cache);
					resolve();
				},
				error: () => {
					// Keep previous cache intact on failure.
					this.loaderService.setLoading(false);
					resolve();
				},
			});
		});
	}

	private masterCacheNeedsCatalogRefresh(): boolean {
		if (!navigator.onLine) {
			return false;
		}

		const raw = localStorage.getItem(SESSION.SESSION_KEY_MASTER_DATA);
		if (!raw) {
			return true;
		}

		try {
			return masterDataNeedsAocCatalogRefresh(JSON.parse(raw) as MasterData);
		} catch {
			return true;
		}
	}

	private hasUsableCache(key: string): boolean {
		const raw = localStorage.getItem(key);
		if (!raw) {
			return false;
		}
		try {
			const parsed = JSON.parse(raw);
			if (parsed == null) {
				return false;
			}
			if (typeof parsed === "object" && !Array.isArray(parsed) && Object.keys(parsed).length === 0) {
				return false;
			}
			return true;
		} catch {
			return false;
		}
	}
}
